import { useMemo } from "react";

/**
 * Pure calculations for a quote (or quote draft state). All values are derived
 * from the form state — no side-effects, no API calls. Used in QuoteForm.
 *
 * Returns: { subtotal, extraTotal, paidMonths, handlingCost, depositCost,
 *   releaseCost, storageTotal, materialsCost, totalBeforeVat, vatAmount,
 *   insuranceAmount, total, balanceDue,
 *   visibleMultiItems, visibleExtraCosts, positiveExtraCount }
 */
export function useQuoteCalculations(form) {
  return useMemo(() => {
    const isLumpSum = form.pricing_type === "lump_sum";
    const lineSubtotal = form.line_items.reduce(
      (s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0
    );
    const multiSubtotal = (form.multi_items || []).reduce((s, mi) => s + (mi.price || 0), 0);
    const isDeposito = form.delivery_type === "deposito";
    const extraLumpSum = (isDeposito && !isLumpSum) ? (parseFloat(form.extra_lump_sum_amount) || 0) : 0;
    const subtotal = isLumpSum
      ? (form.lump_sum_amount || 0)
      : lineSubtotal + multiSubtotal + extraLumpSum;
    const extraTotal = form.extra_costs.reduce((s, ec) => s + (ec.amount || 0), 0);

    const paidMonths = Math.max(0, (form.storage_months || 0) - (form.storage_free_months || 0));
    const handlingCost = form.storage_handling_cost || 0; // informativo
    const depositCost = (form.storage_monthly_cost || 0) * paidMonths;
    const releaseCost = form.storage_release_type && form.storage_release_type !== "ritiro_proprio"
      ? (form.storage_release_price || 0) : 0;
    const storageTotal = depositCost + releaseCost;

    const materialsCost = form.materials_cost || 0;
    const totalBeforeVat = subtotal + extraTotal + storageTotal + materialsCost;
    // Regime IVA: se diverso da "standard", l'aliquota è forzata a 0
    const vatMode = form.vat_mode || "standard";
    const vatZeroed = vatMode !== "standard";
    const effectiveVatRate = vatZeroed ? 0 : (form.vat_rate || 0);
    const vatAmount = totalBeforeVat * effectiveVatRate / 100;
    const insuranceAmount = form.insurance_requested ? (parseFloat(form.insurance_amount) || 0) : 0;
    const total = totalBeforeVat + vatAmount + insuranceAmount;
    const balanceDue = total - (form.deposit || 0);

    const visibleMultiItems = (form.multi_items || []).filter(mi => mi.description && mi.price > 0);
    const visibleExtraCosts = form.extra_costs.filter(ec => ec.description && ec.amount > 0);
    const positiveExtraCount = form.extra_costs.filter(ec => ec.amount > 0).length;

    return {
      isLumpSum, lineSubtotal, multiSubtotal, extraLumpSum, isDeposito,
      subtotal, extraTotal, paidMonths,
      handlingCost, depositCost, releaseCost, storageTotal,
      materialsCost, totalBeforeVat, vatAmount, insuranceAmount,
      total, balanceDue,
      vatMode, vatZeroed, effectiveVatRate,
      visibleMultiItems, visibleExtraCosts, positiveExtraCount,
    };
  }, [form]);
}

/**
 * Pure calculations for a proforma (or proforma draft state). Includes the
 * post-VAT discount logic and the storage_extension fractional months.
 *
 * Returns the same fields as useQuoteCalculations PLUS:
 *   fractionalDeposit, totalWithVat, discountAmount
 */
export function useProformaCalculations(form) {
  return useMemo(() => {
    const isLumpSum = form.pricing_type === "lump_sum";
    const lineSubtotal = form.line_items.reduce(
      (s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0
    );
    const multiSubtotal = (form.multi_items || []).reduce((s, mi) => s + (mi.price || 0), 0);
    const isDeposito = form.delivery_type === "deposito";
    const extraLumpSum = (isDeposito && !isLumpSum) ? (parseFloat(form.extra_lump_sum_amount) || 0) : 0;
    const subtotal = isLumpSum
      ? (form.lump_sum_amount || 0)
      : lineSubtotal + multiSubtotal + extraLumpSum;
    const extraTotal = form.extra_costs.reduce((s, ec) => s + (ec.amount || 0), 0);

    const paidMonths = Math.max(0, (form.storage_months || 0) - (form.storage_free_months || 0));
    const releaseCost = form.storage_release_type && form.storage_release_type !== "ritiro_proprio"
      ? (form.storage_release_price || 0) : 0;
    const depositCost = (form.storage_monthly_cost || 0) * paidMonths;
    // Storage_extension uses fractional months M + R/30
    const extDecimal = form.extension_decimal_months;
    const fractionalDeposit = (extDecimal && extDecimal > 0)
      ? Math.round((form.storage_monthly_cost || 0) * extDecimal * 100) / 100
      : depositCost;
    const storageTotal = fractionalDeposit + releaseCost;

    const materialsCost = form.materials_cost || 0;
    const totalBeforeVat = subtotal + extraTotal + storageTotal + materialsCost;
    // Regime IVA: se diverso da "standard", l'aliquota è forzata a 0
    const vatMode = form.vat_mode || "standard";
    const vatZeroed = vatMode !== "standard";
    const effectiveVatRate = vatZeroed ? 0 : (form.vat_rate || 0);
    const vatAmount = totalBeforeVat * effectiveVatRate / 100;
    const insuranceAmount = form.insurance_requested ? (parseFloat(form.insurance_amount) || 0) : 0;
    const totalWithVat = totalBeforeVat + vatAmount + insuranceAmount;
    const discountAmount = Math.max(0, Math.min(form.discount_amount || 0, totalWithVat));
    const total = totalWithVat - discountAmount;
    const balanceDue = total - (form.deposit || 0);

    const visibleMultiItems = (form.multi_items || []).filter(mi => mi.description && mi.price > 0);
    const visibleExtraCosts = form.extra_costs.filter(ec => ec.description && ec.amount > 0);
    const positiveExtraCount = form.extra_costs.filter(ec => ec.amount > 0).length;

    return {
      isLumpSum, lineSubtotal, multiSubtotal, extraLumpSum, isDeposito,
      subtotal, extraTotal, paidMonths,
      depositCost, fractionalDeposit, releaseCost, storageTotal,
      materialsCost, totalBeforeVat, vatAmount, insuranceAmount,
      totalWithVat, discountAmount, total, balanceDue,
      extDecimal,
      vatMode, vatZeroed, effectiveVatRate,
      visibleMultiItems, visibleExtraCosts, positiveExtraCount,
    };
  }, [form]);
}
